import { useEffect, useState } from 'react';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type BadgeTone,
} from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { SKIP_REASON_LABEL, STOP_REASON_LABEL } from '@/lib/marketing';
import type {
  CampaignRecipientRow,
  CampaignRecipientSend,
  MarketingRecipientStatus,
  Paginated,
} from '@/lib/types';
import { OPENS_ESTIMATE_REASON } from './ReportStatTiles';

/*
 * The campaign's recipients, paginated server-side, with a status filter and
 * a search box (debounced 250ms, resets to page 1). Each recipient carries one
 * line per step: what happened to that send, as labelled badges with the time
 * on each badge's title.
 *
 * `refreshKey` is bumped by the page's live refresh; a refresh keeps the rows
 * on screen rather than fading them back to skeletons.
 */

const PER_PAGE = 25;
const TABLE_STYLE = { minWidth: '760px' };

const STATUS_LABEL: Record<MarketingRecipientStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  stopped: 'Stopped',
};

const STATUS_TONE: Record<MarketingRecipientStatus, BadgeTone> = {
  active: 'info',
  completed: 'success',
  stopped: 'neutral',
};

const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as MarketingRecipientStatus[]).map((value) => ({
  value,
  label: STATUS_LABEL[value],
}));

interface Indicator {
  key: string;
  label: string;
  at: string;
  tone: BadgeTone;
  title?: string;
}

function indicatorsOf(send: CampaignRecipientSend): Indicator[] {
  const out: Indicator[] = [];
  if (send.sentAt) out.push({ key: 'sent', label: 'Sent', at: send.sentAt, tone: 'neutral' });
  if (send.openedAt)
    out.push({ key: 'opened', label: 'Opened', at: send.openedAt, tone: 'neutral', title: OPENS_ESTIMATE_REASON });
  if (send.clickedAt) out.push({ key: 'clicked', label: 'Clicked', at: send.clickedAt, tone: 'neutral' });
  if (send.bouncedAt) out.push({ key: 'bounced', label: 'Bounced', at: send.bouncedAt, tone: 'danger' });
  if (send.repliedAt) out.push({ key: 'replied', label: 'Replied', at: send.repliedAt, tone: 'success' });
  if (send.unsubscribedAt)
    out.push({ key: 'unsubscribed', label: 'Unsubscribed', at: send.unsubscribedAt, tone: 'warning' });
  return out;
}

function SendLine({ send }: { send: CampaignRecipientSend }) {
  const indicators = indicatorsOf(send);
  return (
    <li className="flex flex-wrap items-center gap-1">
      <span className="w-12 shrink-0 text-xs text-fg-muted tabular">Step {send.stepOrder}</span>
      {send.status === 'skipped' && (
        <Badge tone="neutral" variant="outline">
          Skipped{send.skipReason ? ` · ${SKIP_REASON_LABEL[send.skipReason] ?? send.skipReason}` : ''}
        </Badge>
      )}
      {send.status === 'failed' && <Badge tone="danger">Failed</Badge>}
      {(send.status === 'queued' || send.status === 'sending') && (
        <Badge tone="neutral" variant="outline">{send.status === 'queued' ? 'Queued' : 'Sending'}</Badge>
      )}
      {indicators.map((ind) => (
        <Badge
          key={ind.key}
          tone={ind.tone}
          variant="outline"
          title={`${ind.label} ${formatDateTime(ind.at)}${ind.title ? ` — ${ind.title}` : ''}`}
        >
          {ind.label}
        </Badge>
      ))}
    </li>
  );
}

function RecipientCell({ row }: { row: CampaignRecipientRow }) {
  const primary = row.name ?? row.email;
  return (
    <div className="grid min-w-0 gap-0.5">
      {row.contactId !== null ? (
        <TextLink to={`/contacts/${row.contactId}`} className="text-sm font-medium [overflow-wrap:anywhere]">
          {primary}
        </TextLink>
      ) : row.name === null ? (
        <EmailLink address={row.email} className="text-sm font-medium" />
      ) : (
        <span className="text-sm font-medium text-fg [overflow-wrap:anywhere]">{primary}</span>
      )}
      {row.name !== null && <EmailLink address={row.email} tone="muted" className="text-xs" />}
      {row.company && (
        <TextLink to={`/companies/${row.company.id}`} tone="muted" className="text-xs [overflow-wrap:anywhere]">
          {row.company.name}
        </TextLink>
      )}
    </div>
  );
}

function errorText(caught: unknown): string {
  if (caught instanceof ApiError) return caught.formMessage ?? caught.message;
  return caught instanceof Error ? caught.message : 'Failed to load recipients';
}

export function RecipientsTable({ campaignId, refreshKey }: { campaignId: number; refreshKey: number }) {
  // Several statuses at once (owner 2026-10-28), sent as one CSV `status`.
  const [statuses, setStatuses] = useState<string[]>([]);
  const status = statuses.join(',');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [result, setResult] = useState<Paginated<CampaignRecipientRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      // Fires only when the typed text changed, so resetting the page here is
      // always a response to a new search, never to a page click.
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listMarketingCampaignRecipients(
        campaignId,
        {
          page,
          perPage: PER_PAGE,
          ...(status ? { status } : {}),
          ...(search ? { search } : {}),
        },
        controller.signal,
      )
      .then((response) => {
        setResult(response);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorText(caught));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [campaignId, page, status, search, refreshKey]);

  const rows = result?.data ?? [];
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(1, result?.meta.totalPages ?? 1);
  const initialLoad = loading && result === null;
  const filtered = status !== '' || search !== '';

  return (
    <div className="grid min-w-0 gap-0">
      <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3">
        <div className="w-full sm:w-64">
          <Field label="Search recipients" hideLabel>
            {(props) => (
              <Input
                {...props}
                type="search"
                placeholder="Search name or email"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="w-full sm:w-48">
          <Field label="Recipient status" hideLabel>
            {(props) => (
              <MultiCombobox
                id={props.id}
                values={statuses}
                onChange={(values) => {
                  setStatuses(values);
                  setPage(1);
                }}
                options={STATUS_OPTIONS}
                placeholder="Any status"
              />
            )}
          </Field>
        </div>
        {result && (
          <p role="status" aria-live="polite" className="m-0 ml-auto text-xs text-fg-muted tabular">
            {total.toLocaleString()} {total === 1 ? 'recipient' : 'recipients'}
          </p>
        )}
      </div>

      {error && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load recipients.</>}>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
          </Notice>
        </div>
      )}

      {initialLoad ? (
        <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
          <Table style={TABLE_STYLE}>
            <RecipientsHead />
            <TBody>
              {Array.from({ length: 5 }, (_, i) => (
                <TR key={i}>
                  <TD>
                    <div className="grid gap-1.5">
                      <Skeleton className="h-3 w-40" />
                      <Skeleton className="h-2.5 w-52" />
                    </div>
                  </TD>
                  <TD><Skeleton className="h-4 w-20" /></TD>
                  <TD><Skeleton className="h-4 w-48" /></TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      ) : rows.length === 0 ? (
        error ? (
          <EmptyState title="Nothing to show" description="Couldn’t load the recipients — see the message above." />
        ) : (
          <EmptyState
            title={filtered ? 'No recipients match' : 'No recipients yet'}
            description={
              filtered ? 'Clear the search or the status filter to see everyone.' : 'Recipients are added when the campaign starts.'
            }
          />
        )
      ) : (
        <div className={cx('min-w-0', loading && 'opacity-60')} aria-busy={loading || undefined}>
          <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
            <Table style={TABLE_STYLE}>
              <RecipientsHead />
              <TBody>
                {rows.map((row) => (
                  <TR key={row.recipientId}>
                    <TD className="align-top">
                      <RecipientCell row={row} />
                    </TD>
                    <TD className="align-top">
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={STATUS_TONE[row.status]} dot>
                          {STATUS_LABEL[row.status]}
                        </Badge>
                        {row.stopReason && (
                          <Badge tone="neutral" variant="outline">{STOP_REASON_LABEL[row.stopReason] ?? row.stopReason}</Badge>
                        )}
                        {row.abGroup && (
                          <Badge tone="neutral" variant="outline">{row.abGroup === 'holdout' ? 'Holdout' : `Variant ${row.abGroup}`}</Badge>
                        )}
                      </div>
                    </TD>
                    <TD className="align-top">
                      {row.sends.length === 0 ? (
                        <span className="text-xs text-fg-subtle">Nothing sent yet</span>
                      ) : (
                        <ul className="m-0 grid list-none gap-1 p-0">
                          {row.sends.map((send) => (
                            <SendLine key={send.stepOrder} send={send} />
                          ))}
                        </ul>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </div>
      )}

      {result && totalPages > 1 && (
        <nav
          aria-label="Recipients pages"
          className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3"
        >
          <span className="text-xs text-fg-muted tabular">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-1.5">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}

function RecipientsHead() {
  return (
    <THead>
      <TR>
        <TH scope="col" className="w-[38%]">
          Recipient
        </TH>
        <TH scope="col" className="w-48">
          Status
        </TH>
        <TH scope="col">Steps</TH>
      </TR>
    </THead>
  );
}
